import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useFormHook } from '@Hooks/index';
import { EddEmbeddedFields } from '@Pages/happyFlowPages/eddQuestions/pages/embedded/fields.types';
import {
  getDefaultValueByQuestionId,
  saveEddQuestionToLocalStore,
} from '@Pages/happyFlowPages/eddQuestions/shared.utils';
import { EDD_VERSION, EddYesNo } from '@Pages/happyFlowPages/eddQuestions/consts';
import { useBoundStore } from '@Store/useBoundStore';
import { useNavigationManager } from '@Router/useNavigationManager';
import { usePostEddData } from '@Pages/happyFlowPages/eddQuestions/hooks/usePostEddData';
import { RoutesMap } from '@Router/router.types';
import { reportError, ObservabilityErrorSeverity } from '@Services/observability/initObservability';

export const useInvestmentCustodialLogic = () => {
  const { mutateAsync: postEddData, isPending: isPostingEddData } = usePostEddData();
  const { getEddEmbeddedQuestions } = useBoundStore((state) => state);
  const { navigateToNextPage } = useNavigationManager();
  const { t } = useTranslation('eddQuestions');
  const { t: tValidation } = useTranslation('enJSON');

  const formMethods = useFormHook({
    schemaTitle: 'investment-custodial',
    formDefaultValues: {
      [EddEmbeddedFields.EDD_INVESTMENT_ADVICE]:
        getDefaultValueByQuestionId(EddEmbeddedFields.EDD_INVESTMENT_ADVICE) || EddYesNo.NO,
      [EddEmbeddedFields.EDD_CUSTODIAL_SERVICES]:
        getDefaultValueByQuestionId(EddEmbeddedFields.EDD_CUSTODIAL_SERVICES) || EddYesNo.NO,
    },
    formSchema: z.object({
      [EddEmbeddedFields.EDD_INVESTMENT_ADVICE]: z
        .string()
        .min(1, { message: tValidation('validation.required') }),
      [EddEmbeddedFields.EDD_CUSTODIAL_SERVICES]: z
        .string()
        .min(1, { message: tValidation('validation.required') }),
    }),
  });

  const handleSubmit = async () => {
    const investmentValue = formMethods.getValues(EddEmbeddedFields.EDD_INVESTMENT_ADVICE);
    const custodialValue = formMethods.getValues(EddEmbeddedFields.EDD_CUSTODIAL_SERVICES);

    saveEddQuestionToLocalStore({
      question: t('organic.investmentCustodial.investmentTitle'),
      questionId: EddEmbeddedFields.EDD_INVESTMENT_ADVICE,
      answer: JSON.stringify(investmentValue),
    });
    saveEddQuestionToLocalStore({
      question: t('organic.investmentCustodial.custodialTitle'),
      questionId: EddEmbeddedFields.EDD_CUSTODIAL_SERVICES,
      answer: JSON.stringify(custodialValue),
    });

    try {
      await postEddData({
        version: EDD_VERSION,
        results: getEddEmbeddedQuestions(),
      });
      navigateToNextPage(RoutesMap.EDD_INVESTMENT_CUSTODIAL);
    } catch (error) {
      reportError({
        severity: ObservabilityErrorSeverity.ERROR,
        metadata: {
          name: 'EddInvestmentCustodialSubmitError',
          message: error instanceof Error ? error.message : String(error),
        },
        groupingHash: 'edd-submit-error',
      });
    }
  };

  return {
    formMethods,
    handleSubmit,
    isLoading: isPostingEddData,
  };
};
